import React, { useRef, useEffect } from "react";

const hexToRgb = (hex) => {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
};

export const FootballBackground = ({ color1, color2 }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const confettiRef = useRef([]);

  const c1 = hexToRgb(color1);
  const c2 = hexToRgb(color2);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // Generate confetti particles
    const count = 45;
    confettiRef.current = Array.from({ length: count }, () => {
      const useColor1 = Math.random() > 0.5;
      const rgb = useColor1 ? c1 : c2;
      return {
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height - canvas.height,
        w: Math.random() * 5 + 2,
        h: Math.random() * 4 + 1.5,
        r: rgb.r,
        g: rgb.g,
        b: rgb.b,
        alpha: Math.random() * 0.35 + 0.15,
        speed: Math.random() * 0.6 + 0.2,
        drift: (Math.random() - 0.5) * 0.4,
        rotation: Math.random() * 360,
        rotSpeed: (Math.random() - 0.5) * 1.5,
        isWhite: Math.random() > 0.75,
      };
    });

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (const p of confettiRef.current) {
        p.y += p.speed;
        p.x += p.drift;
        p.rotation += p.rotSpeed;

        if (p.y > canvas.height + 20) {
          p.y = -20;
          p.x = Math.random() * canvas.width;
        }

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);

        if (p.isWhite) {
          ctx.fillStyle = `rgba(255, 255, 255, ${p.alpha * 0.7})`;
        } else {
          ctx.fillStyle = `rgba(${p.r}, ${p.g}, ${p.b}, ${p.alpha})`;
        }

        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }

      animRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener("resize", resize);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [c1, c2]);

  return (
    <div
      className="bg-container"
      style={{
        background: `
          radial-gradient(ellipse at 50% 20%, rgba(${c2.r}, ${c2.g}, ${c2.b}, 0.3) 0%, transparent 50%),
          radial-gradient(ellipse at 30% 60%, rgba(${c1.r}, ${c1.g}, ${c1.b}, 0.15) 0%, transparent 40%),
          radial-gradient(ellipse at 70% 60%, rgba(${c1.r}, ${c1.g}, ${c1.b}, 0.1) 0%, transparent 40%),
          linear-gradient(180deg, rgba(${c2.r}, ${c2.g}, ${c2.b}, 0.3) 0%, rgba(${c1.r}, ${c1.g}, ${c1.b}, 0.6) 60%, rgb(${c1.r}, ${c1.g}, ${c1.b}) 100%)
        `,
      }}
    >
      {/* Stadium spotlights */}
      <div
        className="spotlight spotlight-1"
        style={{
          background: `conic-gradient(from 200deg, transparent 0deg, rgba(${c1.r}, ${c1.g}, ${c1.b}, 0.08) 15deg, transparent 30deg)`,
        }}
      />
      <div
        className="spotlight spotlight-2"
        style={{
          background: `conic-gradient(from 330deg, transparent 0deg, rgba(${c2.r}, ${c2.g}, ${c2.b}, 0.06) 15deg, transparent 30deg)`,
        }}
      />
      <div
        className="spotlight spotlight-3"
        style={{
          background: `conic-gradient(from 250deg, transparent 0deg, rgba(${c1.r}, ${c1.g}, ${c1.b}, 0.07) 12deg, transparent 24deg)`,
        }}
      />

      {/* Confetti canvas */}
      <canvas ref={canvasRef} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 3 }} />
    </div>
  );
};

export default FootballBackground;
